import React from 'react';
import { ScaleLinear } from 'd3-scale';
import { useDrawingArea, useXScale } from '@mui/x-charts/hooks';

import { SegmentNode } from 'ff/debrief/features/DebriefSegments';

interface Props {
    currentSegment: SegmentNode;
    segments: SegmentNode[];
}

// Container component for all segment markers
const SegmentMarkers = (props: React.PropsWithChildren<Props>) => {
    return (
        <>
            {props.segments.map((segment, idx, arr) => (
                <SegmentMarker
                    key={segment.id}
                    selected={
                        props.currentSegment?.id === segment?.id ||
                        segment?.hasParentMatching(
                            parent => props.currentSegment?.id === parent.id
                        )
                    }
                    isOnlySelected={arr.length === 1}
                    startTime={segment.getStartTime()}
                    endTime={segment.getEndTime()}
                    label={segment.label}
                    color={segment.getColor()}
                    leftPad={
                        segment.hasScore(true)
                            ? !arr.at(idx - 1)?.hasScore(true)
                            : arr.at(idx - 1)?.hasScore(true)
                    }
                    rightPad={
                        segment.hasScore(true)
                            ? !arr.at(idx + 1)?.hasScore(true)
                            : arr.at(idx + 1)?.hasScore(true)
                    }
                />
            ))}
        </>
    );
};

// Individual segment marker bar
const SegmentMarker = (
    props: React.PropsWithChildren<{
        selected?: boolean;
        isOnlySelected?: boolean;
        startTime: Date;
        endTime: Date;
        label: string;
        color: string;
        leftPad?: boolean;
        rightPad?: boolean;
    }>
) => {
    const drawingArea = useDrawingArea();
    const xScale = useXScale() as ScaleLinear<number, number>;

    const borderRadius = 1.5;
    const padding = 1;

    const zoneTop = props.selected
        ? drawingArea.top + drawingArea.height
        : drawingArea.top + drawingArea.height + 6;
    const zoneRight = props.rightPad
        ? xScale(props.endTime) - padding
        : xScale(props.endTime);
    const zoneBottom = drawingArea.top + drawingArea.height + 12;
    const zoneLeft = props.leftPad
        ? xScale(props.startTime) + padding
        : xScale(props.startTime);

    const pathStart = {
        x: props.leftPad ? zoneLeft + borderRadius : zoneLeft,
        y: zoneTop,
    };

    const zoneWidth = Math.abs(zoneLeft - zoneRight);
    const zoneHeight = Math.abs(zoneTop - zoneBottom);
    let topLineWidth: number;
    if (!props.leftPad && !props.rightPad) {
        topLineWidth = zoneWidth;
    } else if (
        (props.leftPad && !props.rightPad) ||
        (!props.leftPad && props.rightPad)
    ) {
        topLineWidth = zoneWidth - borderRadius;
    } else {
        topLineWidth = zoneWidth - 2 * borderRadius;
    }
    const bottomLineWith = topLineWidth * -1;
    const leftLineHeight =
        (props.leftPad ? zoneHeight - 2 * borderRadius : zoneHeight) * -1;
    const rightLineHeight = props.rightPad
        ? zoneHeight - 2 * borderRadius
        : zoneHeight;

    const topLeftCurve = props.leftPad
        ? `q0,-${borderRadius} ${borderRadius},-${borderRadius}`
        : '';
    const topRightCurve = props.rightPad
        ? `q${borderRadius},0 ${borderRadius},${borderRadius}`
        : ``;
    const bottomRightCurve = props.rightPad
        ? `q 0,${borderRadius} -${borderRadius},${borderRadius}`
        : ``;
    const bottomLeftCurve = props.leftPad
        ? `q -${borderRadius},0 -${borderRadius},-${borderRadius}`
        : ``;

    return (
        <>
            <path
                d={`M${pathStart.x},${pathStart.y} h${topLineWidth} ${topRightCurve} v${rightLineHeight} ${bottomRightCurve} h${bottomLineWith} ${bottomLeftCurve} v${leftLineHeight} ${topLeftCurve}`}
                fill={props.color}
            />
        </>
    );
};

export default SegmentMarkers;
